import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import toast from "react-hot-toast";
import { createField } from "../../redux/features/Admin/Fields/featureAPI";
import {
  Search,
  MapPin,
  Users,
  Euro,
  Calendar,
  Filter,
  X,
  Sparkles,
  Clock,
  Heart,
  Star,
  Wifi,
  Car,
  Coffee,
  Camera,
  Music,
  Utensils,
  TreePine,
  Building,
  Home,
  Palette,
  ChevronDown,
  SlidersHorizontal,
  Zap,
  TrendingUp,
  Award,
  Briefcase,
  Crown,
  Scissors,
  Flower2,
  Mic,
  PartyPopper,
  ChefHat,
  Video,
  Lightbulb,
} from "lucide-react";

interface SmartServiceFiltersProps {
  className?: string;
  onFiltersChange: (filters: any) => void;
  currentFilters?: any;
}

interface QuickServiceFilter {
  id: string;
  label: string;
  icon: any;
  color: string;
  popular?: boolean;
  filters: any;
}

interface ServiceSpecialty {
  id: string;
  label: string;
  icon: any;
  color: string;
  keywords: string[];
}

const SmartServiceFilters: React.FC<SmartServiceFiltersProps> = ({
  className = "",
  onFiltersChange,
  currentFilters = {},
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [activeFilters, setActiveFilters] = useState<any>(currentFilters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [experience, setExperience] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [sortBy, setSortBy] = useState("popularity");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  const [serviceSpecialties, setServiceSpecialties] = useState<ServiceSpecialty[]>([]);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const locationContainerRef = useRef<HTMLDivElement>(null);

  // Google Places Autocomplete
  const { placesService, placePredictions, isPlacePredictionsLoading, getPlacePredictions } = usePlacesService({
    apiKey: process.env.REACT_APP_MAPS_API_KEY,
    options: {
      input: "",
      componentRestrictions: {
        country: "fr",
      },
      types: ["(cities)"],
    },
  });

  // Filtres rapides pour prestataires
  const quickServiceFilters: QuickServiceFilter[] = [
    {
      id: "available_weekend",
      label: "Dispo ce weekend",
      icon: Calendar,
      color: "from-green-500 to-emerald-500",
      popular: true,
      filters: { availableWeekend: true },
    },
    {
      id: "top_rated",
      label: "Mieux notés",
      icon: Star,
      color: "from-yellow-500 to-amber-500",
      popular: true,
      filters: { topRated: true },
    },
    {
      id: "certified",
      label: "Certifiés",
      icon: Award,
      color: "from-blue-500 to-cyan-500",
      filters: { certified: true },
    },
    {
      id: "premium",
      label: "Premium",
      icon: Crown,
      color: "from-purple-500 to-violet-500",
      popular: true,
      filters: { premium: true },
    },
    {
      id: "instant_quote",
      label: "Devis instantané",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      filters: { instantQuote: true },
    },
    {
      id: "trending",
      label: "Tendance",
      icon: TrendingUp,
      color: "from-pink-500 to-rose-500",
      popular: true,
      filters: { trending: true },
    },
    {
      id: "eco_friendly",
      label: "Éco-responsable",
      icon: TreePine,
      color: "from-green-600 to-emerald-600",
      filters: { ecoFriendly: true },
    },
    {
      id: "last_minute",
      label: "Dernière minute",
      icon: Clock,
      color: "from-red-500 to-pink-500",
      filters: { lastMinute: true },
    },
  ];

  // Mapping d'icônes et couleurs par défaut pour les spécialités
  const defaultIconMapping: Record<string, { icon: any; color: string; keywords: string[] }> = {
    traiteur: { icon: ChefHat, color: "text-orange-500", keywords: ["traiteur", "catering", "buffet", "cuisine"] },
    photographe: {
      icon: Camera,
      color: "text-purple-500",
      keywords: ["photo", "photographe", "shooting", "portrait"],
    },
    videaste: { icon: Video, color: "text-red-500", keywords: ["video", "videaste", "film", "montage"] },
    dj: { icon: Music, color: "text-blue-500", keywords: ["dj", "musique", "sono", "animation"] },
    musicien: { icon: Mic, color: "text-green-500", keywords: ["musicien", "groupe", "concert", "live"] },
    decorateur: { icon: Palette, color: "text-pink-500", keywords: ["décor", "decoration", "design", "ambiance"] },
    décorateur: { icon: Palette, color: "text-pink-500", keywords: ["décor", "decoration", "design", "ambiance"] },
    fleuriste: {
      icon: Flower2,
      color: "text-rose-500",
      keywords: ["fleur", "fleuriste", "bouquet", "composition"],
    },
    animateur: {
      icon: PartyPopper,
      color: "text-yellow-500",
      keywords: ["animation", "animateur", "spectacle", "jeux"],
    },
    coiffeur: {
      icon: Scissors,
      color: "text-indigo-500",
      keywords: ["coiffure", "coiffeur", "maquillage", "beauté"],
    },
    eclairage: {
      icon: Lightbulb,
      color: "text-amber-500",
      keywords: ["éclairage", "lumière", "led", "projection"],
    },
    éclairage: {
      icon: Lightbulb,
      color: "text-amber-500",
      keywords: ["éclairage", "lumière", "led", "projection"],
    },
    organisation: {
      icon: Briefcase,
      color: "text-gray-500",
      keywords: ["organisation", "wedding planner", "événementiel", "coordination"],
    },
    transport: { icon: Car, color: "text-blue-600", keywords: ["transport", "voiture", "limousine", "navette"] },
  };

  // Fetch service types from API and build specialties with icons
  useEffect(() => {
    const fetchServiceSpecialties = async () => {
      try {
        const fields = await createField.verify();
        if (Array.isArray(fields)) {
          const specialties: ServiceSpecialty[] = fields
            .filter((field: any) => field.enable !== false)
            .map((field: any) => {
              const fieldKey = field.designation.toLowerCase();
              const mapping = defaultIconMapping[fieldKey];
              return {
                id: fieldKey,
                label: field.designation,
                icon: mapping?.icon || Briefcase, // Icône par défaut si non trouvée
                color: mapping?.color || "text-gray-500", // Couleur par défaut si non trouvée
                keywords: mapping?.keywords || [field.designation.toLowerCase()],
              };
            });
          setServiceSpecialties(specialties);
        }
      } catch (error) {
        console.error("Error fetching service specialties:", error);
        // Fallback to default values if API fails
        const fallbackSpecialties: ServiceSpecialty[] = [
          { id: "traiteur", label: "Traiteur", icon: ChefHat, color: "text-orange-500", keywords: ["traiteur"] },
          {
            id: "photographe",
            label: "Photographe",
            icon: Camera,
            color: "text-purple-500",
            keywords: ["photographe"],
          },
          { id: "dj", label: "DJ", icon: Music, color: "text-blue-500", keywords: ["dj"] },
          {
            id: "decorateur",
            label: "Décorateur",
            icon: Palette,
            color: "text-pink-500",
            keywords: ["decorateur"],
          },
          { id: "fleuriste", label: "Fleuriste", icon: Flower2, color: "text-rose-500", keywords: ["fleuriste"] },
        ];
        setServiceSpecialties(fallbackSpecialties);
      }
    };
    fetchServiceSpecialties();
  }, []);

  // Options de tri spécifiques aux services
  const sortOptions = [
    { value: "popularity", label: "Popularité" },
    { value: "rating", label: "Mieux notés" },
    { value: "price_low", label: "Prix croissant" },
    { value: "price_high", label: "Prix décroissant" },
    { value: "newest", label: "Plus récents" },
    { value: "experience", label: "Plus expérimentés" },
    { value: "distance", label: "Distance" },
  ];

  // Niveaux d'expérience
  const experienceOptions = [
    { value: "", label: "Toute expérience" },
    { value: "junior", label: "Débutant (0-2 ans)" },
    { value: "intermediate", label: "Intermédiaire (2-5 ans)" },
    { value: "senior", label: "Expérimenté (5-10 ans)" },
    { value: "expert", label: "Expert (10+ ans)" },
  ];

  // Handle location input change for autocomplete
  const handleLocationInputChange = (value: string) => {
    setLocation(value);
    if (value.length > 2) {
      getPlacePredictions({ input: value });
      setShowLocationDropdown(true);
    } else {
      setShowLocationDropdown(false);
    }
  };

  // Handle location selection from autocomplete
  const handleLocationSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    setIsLoadingPlaces(true);

    if (!placesService) {
      setIsLoadingPlaces(false);
      return;
    }

    placesService.getDetails({ placeId: prediction.place_id }, (placeDetails: any, status: any) => {
      if (status !== "OK") {
        toast.error("Erreur lors de la sélection du lieu, veuillez réessayer!");
        setIsLoadingPlaces(false);
        return;
      }

      const parser = new DOMParser();
      const adr_address = parser.parseFromString(placeDetails?.adr_address ?? "", "text/html");
      const streetAddress = adr_address.querySelector(".street-address")?.textContent || "";

      let selectedLocation = "";
      if (streetAddress !== placeDetails?.name) {
        selectedLocation = placeDetails?.name;
      } else {
        selectedLocation = streetAddress;
      }

      setLocation(selectedLocation);
      setShowLocationDropdown(false);
      setIsLoadingPlaces(false);
      getPlacePredictions({ input: "" });
    });
  };

  // Handle click outside location dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationContainerRef.current && !locationContainerRef.current.contains(event.target as Node)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format prediction description (remove France)
  const formatPredictionDescription = (description: string) => {
    return description.replace(/,\s*France$/, "").trim();
  };

  const handleQuickFilterToggle = (filterId: string) => {
    const newSelected = selectedQuickFilters.includes(filterId)
      ? selectedQuickFilters.filter((id) => id !== filterId)
      : [...selectedQuickFilters, filterId];

    setSelectedQuickFilters(newSelected);

    // Merge filters from selected quick filters
    const mergedFilters = { ...activeFilters };
    newSelected.forEach((id) => {
      const filter = quickServiceFilters.find((f) => f.id === id);
      if (filter) {
        Object.assign(mergedFilters, filter.filters);
      }
    });

    setActiveFilters(mergedFilters);
    onFiltersChange(mergedFilters);
  };

  const handleSpecialtyToggle = (specialtyId: string) => {
    const newSelected = selectedSpecialties.includes(specialtyId)
      ? selectedSpecialties.filter((id) => id !== specialtyId)
      : [...selectedSpecialties, specialtyId];

    setSelectedSpecialties(newSelected);
    const newFilters = { ...activeFilters, specialties: newSelected };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handlePriceChange = (newRange: number[]) => {
    setPriceRange(newRange);
    const newFilters = { ...activeFilters, minPrice: newRange[0], maxPrice: newRange[1] };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleExperienceChange = (newExperience: string) => {
    setExperience(newExperience);
    const newFilters = { ...activeFilters, experience: newExperience };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSortChange = (newSort: string) => {
    setSortBy(newSort);
    const newFilters = { ...activeFilters, sortBy: newSort };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSearch = () => {
    const newFilters = {
      ...activeFilters,
      search: searchQuery,
      location: location,
    };
    setActiveFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    setSelectedQuickFilters([]);
    setSelectedSpecialties([]);
    setPriceRange([0, 2000]);
    setExperience("");
    setSearchQuery("");
    setLocation("");
    setSortBy("popularity");
    setShowLocationDropdown(false);
    setActiveFilters({});
    onFiltersChange({});
    getPlacePredictions({ input: "" });
  };

  const activeFiltersCount =
    selectedQuickFilters.length +
    selectedSpecialties.length +
    (experience ? 1 : 0) +
    (searchQuery ? 1 : 0) +
    (location ? 1 : 0);

  return (
    <div
      className={`bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 overflow-hidden ${className}`}
      ref={ref}
    >
      {/* Header avec recherche principale */}
      <div className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-700">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {/* Barre de recherche principale */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un prestataire (ex: photographe mariage, DJ...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1 relative" ref={locationContainerRef}>
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                ref={locationInputRef}
                type="text"
                placeholder="Localisation (Paris, Lyon, Marseille...)"
                value={location}
                onChange={(e) => handleLocationInputChange(e.target.value)}
                onFocus={() => location.length > 2 && setShowLocationDropdown(true)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />

              {/* Autocomplete Dropdown */}
              <AnimatePresence>
                {showLocationDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-30 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-60 overflow-y-auto"
                  >
                    {isPlacePredictionsLoading || isLoadingPlaces ? (
                      <div className="flex items-center justify-center py-4 space-x-2">
                        <div className="w-4 h-4 bg-purple-500 rounded-full animate-pulse"></div>
                        <div
                          className="w-4 h-4 bg-purple-500 rounded-full animate-pulse"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                        <div
                          className="w-4 h-4 bg-purple-500 rounded-full animate-pulse"
                          style={{ animationDelay: "0.4s" }}
                        ></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">Recherche...</span>
                      </div>
                    ) : placePredictions.length > 0 ? (
                      <ul className="py-2">
                        {placePredictions.map((prediction, index) => (
                          <motion.li
                            key={prediction.place_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            onClick={() => handleLocationSelect(prediction)}
                            className="px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center space-x-3"
                          >
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatPredictionDescription(prediction.description)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {prediction.structured_formatting?.secondary_text || "France"}
                              </div>
                            </div>
                          </motion.li>
                        ))}
                      </ul>
                    ) : location.length > 2 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                        Aucune localisation trouvée
                      </div>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSearch}
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
            >
              <Search className="w-5 h-5" />
              <span>Rechercher</span>
            </motion.button>
          </div>

          {/* Filtres rapides populaires */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">Filtres populaires:</span>
            </div>
            {quickServiceFilters
              .filter((f) => f.popular)
              .map((filter) => {
                const Icon = filter.icon;
                const isSelected = selectedQuickFilters.includes(filter.id);
                return (
                  <motion.button
                    key={filter.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleQuickFilterToggle(filter.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 text-sm font-medium ${
                      isSelected
                        ? `bg-gradient-to-r ${filter.color} text-white shadow-lg`
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{filter.label}</span>
                  </motion.button>
                );
              })}
          </div>
        </motion.div>
      </div>

      {/* Filtres avancés */}
      <div className="p-6">
        {/* Toggle filtres avancés */}
        <div className="flex items-center justify-between mb-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
          >
            <SlidersHorizontal className="w-5 h-5" />
            <span className="font-medium">Filtres avancés</span>
            <ChevronDown
              className={`w-4 h-4 transform transition-transform ${showAdvanced ? "rotate-180" : ""}`}
            />
          </motion.button>

          <div className="flex items-center space-x-4">
            {activeFiltersCount > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={clearAllFilters}
                className="flex items-center space-x-1 text-red-500 hover:text-red-600 text-sm"
              >
                <X className="w-4 h-4" />
                <span>Effacer tout ({activeFiltersCount})</span>
              </motion.button>
            )}

            {/* Tri */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Trier par:</span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Tous les filtres rapides */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtres rapides
                </h3>
                <div className="flex flex-wrap gap-2">
                  {quickServiceFilters.map((filter) => {
                    const Icon = filter.icon;
                    const isSelected = selectedQuickFilters.includes(filter.id);
                    return (
                      <motion.button
                        key={filter.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleQuickFilterToggle(filter.id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300 text-xs ${
                          isSelected
                            ? `bg-gradient-to-r ${filter.color} text-white shadow-md`
                            : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                        }`}
                      >
                        <Icon className="w-3 h-3" />
                        <span>{filter.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Spécialités */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Spécialités
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {serviceSpecialties.map((specialty) => {
                    const Icon = specialty.icon;
                    const isSelected = selectedSpecialties.includes(specialty.id);
                    return (
                      <motion.button
                        key={specialty.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSpecialtyToggle(specialty.id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 text-xs ${
                          isSelected
                            ? "bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 border-2 border-purple-300"
                            : "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                        }`}
                      >
                        <Icon className={`w-3 h-3 ${specialty.color}`} />
                        <span>{specialty.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Expérience et Prix */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Expérience */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                    <Award className="w-4 h-4 mr-2" />
                    Niveau d'expérience
                  </h3>
                  <select
                    value={experience}
                    onChange={(e) => handleExperienceChange(e.target.value)}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {experienceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Prix */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                    <Euro className="w-4 h-4 mr-2" />
                    Budget (par prestation)
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>{priceRange[0]}€</span>
                      <span>{priceRange[1]}€</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="2000"
                      step="50"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 slider"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SmartServiceFilters;
